import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { HodDashboardService } from '../../../../core/services/gse/hod-dashboard.service';
import { ToastActions } from '../../../toast/toast.actions';
import * as HodDashboardActions from './hod-dashboard.actions';

@Injectable()
export class HodDashboardEffects {
  private actions$ = inject(Actions);
  private hodDashboardService = inject(HodDashboardService);

  loadHodDashboardSummary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HodDashboardActions.loadHodDashboardSummary),
      mergeMap(() =>
        this.hodDashboardService.getHodDashboardSummaryMerged().pipe(
          map((summary) => HodDashboardActions.loadHodDashboardSummarySuccess({ summary })),
          catchError((error) =>
            of(
              HodDashboardActions.loadHodDashboardSummaryFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  'Failed to load HOD dashboard summary',
              })
            )
          )
        )
      )
    )
  );

  loadHodTopTechnicians$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HodDashboardActions.loadHodTopTechnicians),
      mergeMap(({ limit }) =>
        this.hodDashboardService.getTopTechnicians(limit).pipe(
          map((topTechnicians) => HodDashboardActions.loadHodTopTechniciansSuccess({ topTechnicians })),
          catchError((error) =>
            of(
              HodDashboardActions.loadHodTopTechniciansFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  'Failed to load top technicians',
              })
            )
          )
        )
      )
    )
  );

  loadHodMaintenanceWeeklyOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HodDashboardActions.loadHodMaintenanceWeeklyOverview),
      mergeMap(({ from, to }) =>
        this.hodDashboardService.getMaintenanceWeeklyOverview({ from, to }).pipe(
          map((weeklyOverview) =>
            HodDashboardActions.loadHodMaintenanceWeeklyOverviewSuccess({ weeklyOverview })
          ),
          catchError((error) =>
            of(
              HodDashboardActions.loadHodMaintenanceWeeklyOverviewFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  'Failed to load maintenance weekly overview',
              })
            )
          )
        )
      )
    )
  );

  loadHodSparePartUtilization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HodDashboardActions.loadHodSparePartUtilization),
      mergeMap(({ from, to }) =>
        this.hodDashboardService.getSparePartUtilization({ from, to }).pipe(
          map((utilization) =>
            HodDashboardActions.loadHodSparePartUtilizationSuccess({ utilization })
          ),
          catchError((error) =>
            of(
              HodDashboardActions.loadHodSparePartUtilizationFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  'Failed to load spare parts utilization',
              })
            )
          )
        )
      )
    )
  );

  loadHodInventoryCorrections$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HodDashboardActions.loadHodInventoryCorrections),
      mergeMap(({ params }) =>
        this.hodDashboardService.getInventoryCorrections(params).pipe(
          map((res) =>
            HodDashboardActions.loadHodInventoryCorrectionsSuccess({ corrections: res.data ?? [] })
          ),
          catchError((error) =>
            of(
              HodDashboardActions.loadHodInventoryCorrectionsFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  'Failed to load inventory corrections',
              })
            )
          )
        )
      )
    )
  );

  loadHodEquipmentTatOverview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HodDashboardActions.loadHodEquipmentTatOverview),
      mergeMap(({ from, to }) =>
        this.hodDashboardService.getEquipmentTatOverview({ from, to }).pipe(
          map((tatOverview) =>
            HodDashboardActions.loadHodEquipmentTatOverviewSuccess({ tatOverview })
          ),
          catchError((error) =>
            of(
              HodDashboardActions.loadHodEquipmentTatOverviewFailure({
                error:
                  error?.error?.message ||
                  error?.message ||
                  'Failed to load equipment TAT overview',
              })
            )
          )
        )
      )
    )
  );

  loadHodDashboardSummaryFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HodDashboardActions.loadHodDashboardSummaryFailure),
        map(({ error }) =>
          ToastActions.showErrorToast({
            summary: 'Error',
            detail: error,
          })
        )
      ),
    { dispatch: true }
  );

  loadHodTopTechniciansFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HodDashboardActions.loadHodTopTechniciansFailure),
        map(({ error }) =>
          ToastActions.showErrorToast({
            summary: 'Error',
            detail: error,
          })
        )
      ),
    { dispatch: true }
  );

  loadHodMaintenanceWeeklyOverviewFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HodDashboardActions.loadHodMaintenanceWeeklyOverviewFailure),
        map(({ error }) =>
          ToastActions.showErrorToast({
            summary: 'Error',
            detail: error,
          })
        )
      ),
    { dispatch: true }
  );

  loadHodSparePartUtilizationFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HodDashboardActions.loadHodSparePartUtilizationFailure),
        map(({ error }) =>
          ToastActions.showErrorToast({
            summary: 'Error',
            detail: error,
          })
        )
      ),
    { dispatch: true }
  );

  loadHodInventoryCorrectionsFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HodDashboardActions.loadHodInventoryCorrectionsFailure),
        map(({ error }) =>
          ToastActions.showErrorToast({
            summary: 'Error',
            detail: error,
          })
        )
      ),
    { dispatch: true }
  );

  loadHodEquipmentTatOverviewFailureToast$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HodDashboardActions.loadHodEquipmentTatOverviewFailure),
        map(({ error }) =>
          ToastActions.showErrorToast({
            summary: 'Error',
            detail: error,
          })
        )
      ),
    { dispatch: true }
  );
}
